'use client';

import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { PropsWithChildren, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps extends PropsWithChildren {
  content: React.ReactNode;
  trigger?: 'click' | 'hover';
}

const ToolTip = React.forwardRef<HTMLDivElement, TooltipProps>(({ content, children, trigger = 'click' }, ref) => {
  const [visible, setVisible] = useState(false);
  const triggerId = useId();
  const tooltipId = useId();
  const isHover = trigger === 'hover';

  const toggleTooltip = () => {
    setVisible(!visible);
  };

  const closeTooltip = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (isHover) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const parent = document.getElementById(triggerId);
      const child = document.getElementById(tooltipId);

      if (visible && parent && !parent.contains(target) && child && !child.contains(target)) {
        setVisible(false);
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [visible, triggerId, tooltipId, isHover]);

  useEffect(() => {
    if (!visible) return;

    const parent = document.getElementById(triggerId);
    const child = document.getElementById(tooltipId);

    if (!child || !parent) return;

    const positionTooltip = () => {
      const parentRect = parent.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16;

      const centeredLeft = parentRect.left + (parentRect.width - childRect.width) / 2;

      let left: number;

      if (centeredLeft < padding) {
        left = padding;
      } else if (centeredLeft + childRect.width > viewportWidth - padding) {
        left = Math.max(padding, viewportWidth - padding - childRect.width);
      } else {
        left = centeredLeft;
      }

      const spacing = 8;
      const top = parentRect.bottom + spacing;

      child.style.top = `${top}px`;
      child.style.left = `${left}px`;
    };

    positionTooltip();

    const handleUpdate = () => positionTooltip();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [visible, triggerId, tooltipId]);

  return (
    <>
      <span
        id={triggerId}
        aria-describedby={visible ? tooltipId : undefined}
        {...(isHover
          ? {
              onMouseEnter: () => setVisible(true),
              onMouseLeave: () => setVisible(false),
              className: 'relative inline-block cursor-help',
            }
          : {
              onClick: toggleTooltip,
              className: 'relative inline-block cursor-pointer',
            })}
      >
        {children}
      </span>

      {visible &&
        createPortal(
          isHover ? (
            <div
              id={tooltipId}
              role="tooltip"
              ref={ref}
              className="pointer-events-none fixed z-[100] max-w-xs rounded-lg bg-white p-3 shadow-lg"
            >
              {content}
            </div>
          ) : (
            <>
              {/* Mobile overlay */}
              <div className="bg-black/50 fixed inset-0 z-[100] sm:hidden" onClick={closeTooltip} />

              {/* Tooltip */}
              <div
                id={tooltipId}
                role="tooltip"
                ref={ref}
                className="fixed z-[100] min-w-[300px] max-w-[501px] rounded-2xl bg-white p-6 shadow-lg"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 pr-2">{content}</div>
                  <button
                    onClick={closeTooltip}
                    className="text-black hover:text-gray-600 flex-shrink-0 transition-colors"
                    aria-label="Close"
                  >
                    <XMarkIcon className="size-6" />
                  </button>
                </div>
              </div>
            </>
          ),
          document.body,
        )}
    </>
  );
});

ToolTip.displayName = 'Tooltip';

export default ToolTip;
